import * as React from 'react'

import { Box, Button, Icon, Radio, Text } from '@factorialco/deprecated-design-system'
import { FormikProps } from 'formik'
import { AttendanceEnumsPoolTypeEnum } from 'generated/resources/graphql'

import Callout from 'modules/core/components/Callout'
import Modal from 'modules/core/components/Modal'
import i18n from 'modules/core/lib/i18n'

import { CustomTimeRangesFormData } from 'modules/attendance/lib/forms/utils/CustomTimeRangesFormData'

import { Step } from './utils/stepTypes'

interface SecondNewTimeBlockModalProps {
  form: FormikProps<CustomTimeRangesFormData>
  onClose: () => void
  onSubmit: () => void
  goBack: () => void
  totalSteps: number
  step: Step
}

const CompensationTimeBlockModal: React.FC<SecondNewTimeBlockModalProps> = ({
  form,
  onClose,
  onSubmit,
  goBack,
  step,
  totalSteps,
}) => {
  const [balanceAlert, setBalanceAlert] = React.useState(form.values.pool_type === 'balance')
  const [displayError, setDisplayError] = React.useState(false)

  const allowedPoolTypes = ['balance', 'virtual_payroll', 'virtual_timeoff']

  const selectedPoolType = form.values.pool_type ?? ''
  const onContinue = () => {
    const poolTypeSelected = allowedPoolTypes.includes(selectedPoolType)

    if (!poolTypeSelected) {
      setDisplayError(true)
      return
    }

    onSubmit()
  }

  const footer = (
    <>
      <Button type="secondary" onClick={() => goBack()}>
        {i18n.t('common.back')}
      </Button>
      <Button onClick={onContinue}>{i18n.t('common.save')}</Button>
    </>
  )

  const onChange = (value: string) => {
    form.setFieldValue('pool_type', value)

    setDisplayError(!allowedPoolTypes.includes(value as AttendanceEnumsPoolTypeEnum))
    setBalanceAlert(value === 'balance')
  }

  return (
    <Modal
      illustration="night"
      title={i18n.t('time_tracking.settings.time_blocks.categories.modals.compensation_with')}
      description=""
      onClose={onClose}
      onSubmit={onContinue}
      footer={footer}
      progress={{ amount: step, total: totalSteps }}
    >
      <Box gap="s16">
        <Radio
          name={'virtual_timeoff'}
          option={{
            label: (
              <Box flexDirection="row" gap="s2">
                <Icon.Medium icon="clock" color="grey800" />
                <Text size="300" weight="medium" color="grey800">
                  {i18n.t('time_tracking.settings.time_blocks.categories.modals.time_off')}
                </Text>
              </Box>
            ),
            value: 'virtual_timeoff',
          }}
          onChange={(value: string) => onChange(value)}
          value={selectedPoolType}
          description={i18n.t(
            'time_tracking.settings.time_blocks.categories.modals.time_off_description'
          )}
        />
        <Radio
          name={'virtual_payroll'}
          option={{
            label: (
              <Box flexDirection="row" gap="s2">
                <Icon.Medium icon="money-bag" color="grey800" />
                <Text size="300" weight="medium" color="grey800">
                  {i18n.t('time_tracking.settings.time_blocks.categories.modals.payroll')}
                </Text>
              </Box>
            ),
            value: 'virtual_payroll',
          }}
          onChange={(value: string) => onChange(value)}
          value={selectedPoolType}
          description={i18n.t(
            'time_tracking.settings.time_blocks.categories.modals.payroll_description'
          )}
        />
        <Radio
          name={'balance'}
          option={{
            label: (
              <Box flexDirection="row" gap="s2">
                <Icon.Medium icon="balance" color="grey800" />
                <Text size="300" weight="medium" color="grey800">
                  {i18n.t('time_tracking.settings.time_blocks.categories.modals.balance_v2')}
                </Text>
              </Box>
            ),
            value: 'balance',
          }}
          onChange={(value: string) => onChange(value)}
          value={selectedPoolType}
          description={i18n.t(
            'time_tracking.settings.time_blocks.categories.modals.balance_description_v2'
          )}
        />
        {balanceAlert && (
          <Callout
            type="warning"
            title={i18n.t(
              'time_tracking.settings.time_blocks.categories.modals.balance_callout_title'
            )}
            description={i18n.t(
              'time_tracking.settings.time_blocks.categories.modals.balance_callout_description'
            )}
            onRemove={() => setBalanceAlert(false)}
          />
        )}

        {displayError && (
          <Box paddingLeft="s2">
            <Text size="200" color="radicalRed1000" capitalize>
              {i18n.t(
                'time_tracking.settings.time_blocks.categories.modals.errors.pool_type_missing'
              )}
            </Text>
          </Box>
        )}
      </Box>
    </Modal>
  )
}

export default CompensationTimeBlockModal
